import { useCallback, useRef } from 'react';

import { CfsApi } from '@/api';
import { useCFSNavigationStore } from '@/store';

/**
 * Hook for prefetching CFS data on hover to improve navigation performance.
 * Caches fetched CFS data in the navigation store for instant access.
 */
export const useCFSPrefetch = () => {
  // Track in-flight prefetch requests to avoid duplicates
  const pendingPrefetches = useRef<Set<string>>(new Set());

  const prefetch = useCallback(async (cfsId: string) => {
    // Skip if already cached
    const cached = useCFSNavigationStore.getState().getCFSFromCache(cfsId);
    if (cached) return;

    // Skip if already fetching
    if (pendingPrefetches.current.has(cfsId)) return;

    pendingPrefetches.current.add(cfsId);

    try {
      const response = await CfsApi.getOne(cfsId);
      useCFSNavigationStore.getState().setCFSCache(cfsId, response.data);
    } catch {
      // Silently fail prefetch - user will fetch on navigation if needed
    } finally {
      pendingPrefetches.current.delete(cfsId);
    }
  }, []);

  return { prefetch };
};
